import { FormEvent, useEffect, useState } from 'react';
import { AuthProps } from '@/components/page/authProps';
import LoginFormView from './LoginFormView';
import RefreshAuthView from './RefreshAuthView';

type Props = {
  authProps?: AuthProps;
}

export default function LoginForm({ authProps }: Props) {
  const [ refreshingTokens, setRefreshingTokens ] = useState(authProps?.shouldRefresh || false);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ errorMessage, setErrorMessage ] = useState('');

  useEffect(() => {
    if (!refreshingTokens) {
      return;
    }

    if (!(authProps && authProps.email && authProps.username && authProps.refreshToken)) {
      setRefreshingTokens(false);
      return;
    }

    (async () => {
      // Attempt to refresh auth tokens.
      const authResponse = await fetch('/api/puma/cognito/refresh', {
        method: 'POST',
        body: JSON.stringify({
          email: authProps.email,
          username: authProps.username,
          refreshToken: authProps.refreshToken,
        }),
      });

      const authData = await authResponse.json();

      if (authData.error) {
        setRefreshingTokens(false);
        return;
      }

      // Login refresh succeeded. Reload page.
      window.location.reload();
    })();
  }, [ authProps, refreshingTokens ]);

  if (refreshingTokens) {
    // If we can refresh auth tokens, then don't show the login form.
    // Allow the useEffect to attempt to refresh tokens first.
    return <RefreshAuthView/>;
  }

  // Can not refresh tokens, show the login form.
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    if (isLoading) {
      // Don't allow multiple attempts.
      return;
    }

    setErrorMessage('');
    setIsLoading(true);

    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const authResponse = await fetch('/api/puma/cognito/login', {
      method: 'POST',
      body: JSON.stringify({
        email: formData.get('email') || '',
        password: formData.get('password') || '',
      }),
    });

    const authData = await authResponse.json();
    setIsLoading(false);

    if (authData.error) {
      setErrorMessage(authData.error.message);
      return;
    }

    // Login succeeded. Reload page.
    window.location.reload();
  };

  return <LoginFormView onSubmit={onSubmit} isLoading={isLoading} errorMessage={errorMessage}/>;
}
